// Game Level Definitions
// Each level defines parameters for target spawning, scoring, and difficulty.
// Follows sawtooth difficulty curve: each level starts slightly easier than
// the previous level's peak, then ramps up through its waves.

const LEVELS = [
    // ─── Act 1: Learn the basics ────────────────────────────────────────
    {
        id: 1,
        name: 'First Focus',
        targetSize: 1.0,        // multiplier of base size
        duration: 2.5,          // seconds before target expires
        spawnRate: 1.5,         // seconds between spawns
        spawnArea: 0.4,         // fraction of screen (centered)
        maxSimultaneous: 1,
        decoyChance: 0,
        goldenChance: 0,
        movingChance: 0,
        movingSpeed: 0,
        istutorial: true,       // progressive HUD reveal
    },
    {
        id: 2,
        name: 'Quick Eyes',
        targetSize: 0.875,
        duration: 2.0,
        spawnRate: 1.3,
        spawnArea: 0.5,
        maxSimultaneous: 1,
        decoyChance: 0,
        goldenChance: 0,
        movingChance: 0,
        movingSpeed: 0,
    },
    {
        id: 3,
        name: 'Combo Starter',
        targetSize: 0.8125,
        duration: 1.8,
        spawnRate: 1.2,
        spawnArea: 0.6,
        maxSimultaneous: 1,
        decoyChance: 0,
        goldenChance: 0,
        movingChance: 0,
        movingSpeed: 0,
    },
    // ─── Act 2: New target types ────────────────────────────────────────
    {
        id: 4,
        name: 'Look Sharp',
        targetSize: 0.75,
        duration: 1.6,
        spawnRate: 1.1,
        spawnArea: 0.7,
        maxSimultaneous: 1,
        decoyChance: 0.10,
        goldenChance: 0,
        movingChance: 0,
        movingSpeed: 0,
    },
    {
        id: 5,
        name: 'Double Vision',
        targetSize: 0.6875,
        duration: 1.5,
        spawnRate: 1.0,
        spawnArea: 0.8,
        maxSimultaneous: 2,
        decoyChance: 0.15,
        goldenChance: 0,
        movingChance: 0,
        movingSpeed: 0,
    },
    {
        id: 6,
        name: 'Spread Out',
        targetSize: 0.75,
        duration: 1.8,
        spawnRate: 1.1,
        spawnArea: 0.85,
        maxSimultaneous: 2,
        decoyChance: 0.10,
        goldenChance: 0,
        movingChance: 0,
        movingSpeed: 0,
    },
    {
        id: 7,
        name: 'Drifters',
        targetSize: 0.8125,
        duration: 2.0,
        spawnRate: 1.2,
        spawnArea: 0.7,
        maxSimultaneous: 1,
        decoyChance: 0,
        goldenChance: 0,
        movingChance: 0.40,
        movingSpeed: 30,         // px/s — slow drift
    },
    {
        id: 8,
        name: 'Mixed Signals',
        targetSize: 0.6875,
        duration: 1.6,
        spawnRate: 1.0,
        spawnArea: 0.75,
        maxSimultaneous: 2,
        decoyChance: 0.12,
        goldenChance: 0,
        movingChance: 0.25,
        movingSpeed: 35,
    },
    // ─── Act 3: Golden targets + speed ──────────────────────────────────
    {
        id: 9,
        name: 'Gold Rush',
        targetSize: 0.75,
        duration: 1.5,
        spawnRate: 1.1,
        spawnArea: 0.7,
        maxSimultaneous: 1,
        decoyChance: 0.05,
        goldenChance: 0.12,
        movingChance: 0.15,
        movingSpeed: 30,
    },
    {
        id: 10,
        name: 'Speed Demon',
        targetSize: 0.625,
        duration: 1.3,
        spawnRate: 0.9,
        spawnArea: 0.8,
        maxSimultaneous: 2,
        decoyChance: 0.10,
        goldenChance: 0.08,
        movingChance: 0.35,
        movingSpeed: 50,         // faster moving targets
    },
    {
        id: 11,
        name: 'Tiny Targets',
        targetSize: 0.5625,
        duration: 1.5,
        spawnRate: 1.0,
        spawnArea: 0.75,
        maxSimultaneous: 2,
        decoyChance: 0.10,
        goldenChance: 0.06,
        movingChance: 0.20,
        movingSpeed: 35,
    },
    // ─── Act 4: Everything combined ─────────────────────────────────────
    {
        id: 12,
        name: 'Triple Threat',
        targetSize: 0.625,
        duration: 1.4,
        spawnRate: 0.85,
        spawnArea: 0.85,
        maxSimultaneous: 3,
        decoyChance: 0.12,
        goldenChance: 0.08,
        movingChance: 0.30,
        movingSpeed: 45,
    },
    {
        id: 13,
        name: 'No Mercy',
        targetSize: 0.5625,
        duration: 1.2,
        spawnRate: 0.8,
        spawnArea: 0.9,
        maxSimultaneous: 3,
        decoyChance: 0.15,
        goldenChance: 0.06,
        movingChance: 0.35,
        movingSpeed: 50,
    },
    {
        id: 14,
        name: 'Chaos Theory',
        targetSize: 0.5,
        duration: 1.1,
        spawnRate: 0.75,
        spawnArea: 0.9,
        maxSimultaneous: 3,
        decoyChance: 0.18,
        goldenChance: 0.10,
        movingChance: 0.45,
        movingSpeed: 55,
    },
    {
        id: 15,
        name: 'Final Boss',
        targetSize: 0.5,
        duration: 1.0,
        spawnRate: 0.7,
        spawnArea: 0.95,
        maxSimultaneous: 4,
        decoyChance: 0.20,
        goldenChance: 0.12,
        movingChance: 0.50,
        movingSpeed: 60,
    },
];

// Wave structure per level (seconds)
const WAVE_TIMING = {
    warmup: 15,
    wave1: 25,
    wave2: 25,
    wave3: 25,
    cooldown: 10,
};

const TOTAL_GAMEPLAY_TIME = Object.values(WAVE_TIMING).reduce((a, b) => a + b, 0); // 100s

// ─── Endless Mode Configuration ─────────────────────────────────────────────

const ENDLESS_TIMERS = [60, 90, 120];

function getEndlessConfig(timerSeconds) {
    return {
        id: 'endless',
        name: 'Endless',
        targetSize: 0.625,
        duration: 1.5,
        spawnRate: 1.0,
        spawnArea: 0.85,
        maxSimultaneous: 2,
        decoyChance: 0.12,
        goldenChance: 0.10,
        movingChance: 0.30,
        movingSpeed: 40,
        isEndless: true,
        timerSeconds: timerSeconds || 90,
    };
}

// Endless mode difficulty ramps over time
function getEndlessDifficultyScale(elapsedTime) {
    // Difficulty increases every 30 seconds
    const phase = Math.floor(elapsedTime / 30);
    return Math.min(1.0 + phase * 0.1, 2.0); // caps at 2x
}

// ─── Daily Challenge ────────────────────────────────────────────────────────

const DAILY_CHALLENGES = [
    { name: 'Speed Blitz', desc: '30 targets in 45 seconds', targetSize: 0.75, duration: 1.2, spawnRate: 1.5, spawnArea: 0.6, maxSimultaneous: 1, decoyChance: 0, goldenChance: 0, movingChance: 0, movingSpeed: 0, timerSeconds: 45 },
    { name: 'Tiny Targets', desc: 'Small targets, big rewards', targetSize: 0.4, duration: 2.0, spawnRate: 1.2, spawnArea: 0.7, maxSimultaneous: 1, decoyChance: 0, goldenChance: 0.15, movingChance: 0, movingSpeed: 0, timerSeconds: 60 },
    { name: 'Dodge Red', desc: 'Decoys everywhere', targetSize: 0.75, duration: 1.5, spawnRate: 0.9, spawnArea: 0.8, maxSimultaneous: 2, decoyChance: 0.35, goldenChance: 0.05, movingChance: 0, movingSpeed: 0, timerSeconds: 60 },
    { name: 'Moving Day', desc: 'Everything moves', targetSize: 0.75, duration: 2.0, spawnRate: 1.0, spawnArea: 0.8, maxSimultaneous: 2, decoyChance: 0, goldenChance: 0.08, movingChance: 0.80, movingSpeed: 50, timerSeconds: 60 },
    { name: 'Gold Hunt', desc: 'Extra golden targets', targetSize: 0.6875, duration: 1.5, spawnRate: 1.0, spawnArea: 0.75, maxSimultaneous: 1, decoyChance: 0.10, goldenChance: 0.25, movingChance: 0.15, movingSpeed: 35, timerSeconds: 60 },
    { name: 'Triple Rush', desc: '3 targets at once', targetSize: 0.625, duration: 1.3, spawnRate: 0.7, spawnArea: 0.85, maxSimultaneous: 3, decoyChance: 0.10, goldenChance: 0.08, movingChance: 0.25, movingSpeed: 40, timerSeconds: 75 },
    { name: 'Precision', desc: 'Accuracy is everything', targetSize: 0.5, duration: 2.5, spawnRate: 1.5, spawnArea: 0.9, maxSimultaneous: 1, decoyChance: 0.20, goldenChance: 0.05, movingChance: 0.10, movingSpeed: 25, timerSeconds: 60 },
];

function getDailyChallenge() {
    // Seed from date string so all users get the same challenge
    const dateStr = new Date().toDateString();
    let hash = 0;
    for (let i = 0; i < dateStr.length; i++) {
        hash = ((hash << 5) - hash) + dateStr.charCodeAt(i);
        hash |= 0;
    }
    const index = Math.abs(hash) % DAILY_CHALLENGES.length;
    const challenge = DAILY_CHALLENGES[index];
    return {
        ...challenge,
        id: 'daily',
        isDaily: true,
        date: dateStr,
    };
}

function isDailyChallengeCompleted() {
    try {
        const data = localStorage.getItem('eyeTrainer_focusCatcher');
        if (!data) return false;
        const parsed = JSON.parse(data);
        return parsed.lastDailyDate === new Date().toDateString();
    } catch (e) {
        return false;
    }
}

function getLevel(id) {
    if (id === 'endless') return getEndlessConfig();
    if (id === 'daily') return getDailyChallenge();
    return LEVELS.find(l => l.id === id) || LEVELS[0];
}

function getLevelCount() {
    return LEVELS.length;
}

function getAllLevels() {
    return LEVELS;
}

// Returns wave difficulty multiplier (1.0 = base, higher = harder within level)
function getWaveMultiplier(elapsedTime) {
    let t = elapsedTime;
    if (t < WAVE_TIMING.warmup) return 0.8;                          // warmup: easier
    t -= WAVE_TIMING.warmup;
    if (t < WAVE_TIMING.wave1) return 1.0;                           // wave1: base
    t -= WAVE_TIMING.wave1;
    if (t < WAVE_TIMING.wave2) return 1.15;                          // wave2: harder
    t -= WAVE_TIMING.wave2;
    if (t < WAVE_TIMING.wave3) return 1.3;                           // wave3: peak
    return 0.85;                                                      // cooldown: easy
}

function getCurrentWave(elapsedTime) {
    let t = elapsedTime;
    if (t < WAVE_TIMING.warmup) return { name: 'warmup', index: 0, total: 4 };
    t -= WAVE_TIMING.warmup;
    if (t < WAVE_TIMING.wave1) return { name: 'wave1', index: 1, total: 4 };
    t -= WAVE_TIMING.wave1;
    if (t < WAVE_TIMING.wave2) return { name: 'wave2', index: 2, total: 4 };
    t -= WAVE_TIMING.wave2;
    if (t < WAVE_TIMING.wave3) return { name: 'wave3', index: 3, total: 4 };
    return { name: 'cooldown', index: 4, total: 4 };
}

function isLevelComplete(elapsedTime) {
    return elapsedTime >= TOTAL_GAMEPLAY_TIME;
}

export {
    LEVELS,
    WAVE_TIMING,
    TOTAL_GAMEPLAY_TIME,
    ENDLESS_TIMERS,
    getLevel,
    getLevelCount,
    getAllLevels,
    getEndlessConfig,
    getEndlessDifficultyScale,
    getDailyChallenge,
    isDailyChallengeCompleted,
    getWaveMultiplier,
    getCurrentWave,
    isLevelComplete,
};
